import { makeStyles, Theme, createStyles, Grid, Typography, Button } from "@material-ui/core";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import useLandingFunctions from "../../../../../hooks/useLandingFunctions";
import { PFXBasicBasicArrangementElement } from "../../../../../models/Interfaces";
import {
  BODY_FONTSIZE_BASE,
  BUTTON_FONTSIZE_BASE,
  BUTTON_PADD_BASE,
  DESKTOP_TEXT_MULTIPLIER,
  MARGIN_BASE,
  TITLE_FONTSIZE_BASE,
} from "../../../../../pages/Landing";
import { RootState } from "../../../../../redux/rootReducer";
import { selectIsMobile } from "../../../../../redux/slices/cleverlynkSlice";
import { selectComponentById, selectComponentDimensions } from "../../../../../redux/slices/landingSlice";

interface IGoToMenu {
  id: string;
}

const GoToMenu = ({ id }: IGoToMenu) => {
  const { width, height } = useSelector(selectComponentDimensions);
  const component = useSelector((state: RootState) => selectComponentById(state, id));

  const { handleGoToCatalog } = useLandingFunctions();
  const isMobile = useSelector(selectIsMobile);

  const componentStyles = useMemo(
    () => ({
      title: {
        fontSize: `${
          (component?.data?.title?.fontSizeMultiplier ?? 1 * TITLE_FONTSIZE_BASE) *
          (isMobile ? 1 : DESKTOP_TEXT_MULTIPLIER)
        }rem`,
        color: component?.data.title?.color ?? "#1a1a1a",
        textAlign: (component?.data.title?.alignment ?? "center") as any,
        fontWeight: component?.data.title?.fontWeight as any,
        fontFamily: component?.data.title?.fontFamily,
      },
      body: {
        fontSize: `${
          (component?.data.body?.fontSizeMultiplier ?? 1 * BODY_FONTSIZE_BASE) *
          (isMobile ? 1 : DESKTOP_TEXT_MULTIPLIER)
        }rem`,
        color: component?.data.body?.color ?? "#1a1a1a",
        textAlign: (component?.data.body?.alignment ?? "center") as any,
        fontWeight: component?.data.body?.fontWeight as any,
        fontFamily: component?.data.body?.fontFamily,
      },
      button: {
        backgroundColor: component?.data.button?.backgroundColor,
        color: component?.data.button?.text?.color ?? "#1a1a1a",
        borderRadius: component?.data.button?.borderRadius,
        padding: (component?.data.button?.sizeMultiplier ?? 1) * BUTTON_PADD_BASE,
        fontSize: `${component?.data.button?.text?.fontSizeMultiplier ?? 1 * BUTTON_FONTSIZE_BASE}rem`,
        fontWeight: component?.data.button?.text?.fontWeight as any,
        fontFamily: component?.data.button?.text?.fontFamily,
        height: 1,
      },
    }),
    [component, isMobile]
  );

  const shouldRenderTitle = useMemo(
    () => component?.data.arrangement.includes(PFXBasicBasicArrangementElement.TITLE),
    [component?.data.arrangement]
  );
  const shouldRenderBody = useMemo(
    () => component?.data.arrangement.includes(PFXBasicBasicArrangementElement.BODY),
    [component?.data.arrangement]
  );
  const shouldRenderButton = useMemo(
    () => component?.data.arrangement.includes(PFXBasicBasicArrangementElement.BUTTON),
    [component?.data.arrangement]
  );

  const handleClickButton = () => {
    if (!!component?.data?.button?.url) {
      window.open(component?.data?.button?.url, "_blank");
    } else handleGoToCatalog();
  };

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        minHeight: height * (component?.data?.heightMultiplier ?? 1),
        backgroundColor: component?.data.backgroundColor ?? "white",
        backgroundImage: component?.data.backgroundImage !== "" ? `url(${component?.data.backgroundImage})` : undefined,
        backgroundSize: "cover",
        padding: `${(component?.data.margins.y ?? 2) * MARGIN_BASE}px ${
          (component?.data.margins.x ?? 2) * MARGIN_BASE
        }px`,
        position: "relative",
      },
      container: {
        width,
      },
      opaqueDiv: {
        position: "absolute",
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        backgroundColor: !!component?.data?.opaqueBackground
          ? `${component?.data?.opaqueBackground.color}${component?.data?.opaqueBackground.alpha}`
          : undefined,
        zIndex: 1,
      },
      body: { ...componentStyles.body },
      title: { ...componentStyles.title },
      button: {
        ...componentStyles.button,
        backgroundColor: componentStyles.button.backgroundColor,
        "&:hover": {
          backgroundColor: `${componentStyles.button.backgroundColor}8c`,
        },
        textTransform: "none",
      },
      buttonTextInput: { ...componentStyles.button, textAlign: "center" },
      bodyTextInput: { ...componentStyles.body, height: "100%" },
      titleTextInput: { ...componentStyles.title, height: "100%" },
      buttonLabel: {
        fontFamily: componentStyles.button.fontFamily,
        fontSize: componentStyles.button.fontSize,
        fontWeight: componentStyles.button.fontWeight,
      },
      titleDiv: {
        // minHeight: `calc(${height / 5}px - 13px)`,
        width: "100%",
        borderRadius: 5,
        zIndex: 2,
      },
      titleInput: {
        // minHeight: `calc(${height / 5}px - 13px)`,
        width: "100%",
        borderRadius: 5,
      },
      bodyDiv: {
        // minHeight: `calc(${height / 2.2}px - 13px)`,
        width: "100%",
        borderRadius: 5,
        marginTop: 8,
        zIndex: 2,
        whiteSpace: "break-spaces",
      },
      bodyInput: {
        // height: `calc(${height / 2.2}px - 13px)`,
        width: "100%",
        borderRadius: 5,
      },
      buttonDiv: {
        // height: `calc(${height / 5}px - 13px)`,
        width: "100%",
        borderRadius: 5,
        zIndex: 2,
      },
      buttonInput: {
        border: "none",
      },
    })
  );

  const classes = useStyles();

  const contentToRender = useMemo(() => {
    return {
      button: component?.data.button.text.content,
      title: component?.data.title.content,
      body: component?.data.body.content,
    };
  }, [component]);

  return (
    <Grid container className={classes.root} id={`landing-component-${id}`} justifyContent="center">
      <div className={classes.opaqueDiv}></div>
      <Grid
        alignItems="center"
        item
        xs={12}
        sm={10}
        md={9}
        direction="column"
        container
        className={classes.container}
        justifyContent={component?.data?.verticalAlignment ?? "center"}>
        {shouldRenderTitle && (
          <Grid
            item
            className={classes.titleDiv}
            id={`title-${id}`}
            // onClick={e => toggleAnchorElem(e, `title-${id}`, component?.data?.title?.content)}
          >
            {/* <LazyShow> */}
            <Typography className={classes.title}>{contentToRender.title}</Typography> {/* </LazyShow> */}
          </Grid>
        )}
        {shouldRenderBody && (
          <Grid item className={classes.bodyDiv} id={`body-${id}`}>
            <Typography className={classes.body}>{contentToRender.body}</Typography>
          </Grid>
        )}
        {shouldRenderButton && (
          <Grid
            item
            className={classes.buttonDiv}
            id={`button-${id}`}
            container
            justifyContent="center"
            alignItems="center">
            <Button className={classes.button} onClick={handleClickButton}>
              <Typography className={classes.buttonLabel}>{contentToRender.button}</Typography>
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
export default GoToMenu;
